// @ts-check
import { X } from 'lucide-react';
import Modal from './Modal';

/**
 * Shared "preview before you commit" modal shell — used by any content editor
 * (site content, email templates, ...) that wants to show what will actually
 * render before the admin saves it.
 *
 * Two modes:
 *  - Draft preview (`onConfirmSave` provided): shown before a save actually
 *    happens. Footer offers "Back to Edit" / "Confirm & Save" so nothing is
 *    persisted until the admin has seen it rendered.
 *  - Read-only preview (`onConfirmSave` omitted): just a "Close" button, for
 *    previewing something already saved.
 *
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   onClose: () => void,
 *   onConfirmSave?: () => void,
 *   saving?: boolean,
 *   confirmLabel?: string,
 *   children: React.ReactNode,
 * }} props
 */
const PreviewModal = ({
  title,
  subtitle,
  onClose,
  onConfirmSave,
  saving = false,
  confirmLabel = 'Confirm & Save',
  children,
}) => {
  const isDraft = Boolean(onConfirmSave);

  return (
    <Modal
      maxWidth="4xl"
      zIndexClass="z-[60]"
      onClose={onClose}
      closeOnBackdrop={false}
      className="rounded-lg shadow-xl flex flex-col"
    >
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border">
        <div>
          <h3 className="text-lg font-medium">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close preview">
          <X size={20} />
        </button>
      </div>

      {isDraft && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-2.5 text-xs text-amber-800 dark:bg-amber-950 dark:border-amber-900 dark:text-amber-200">
          This is a preview of unsaved changes — nothing has been published yet. Check it looks right, then confirm to
          save.
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-6 bg-muted/50">{children}</div>

      <div className="px-4 sm:px-6 py-4 bg-muted/50 border-t border-border flex justify-end gap-3">
        {onConfirmSave ? (
          <>
            <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-accent">
              Back to Edit
            </button>
            <button
              onClick={onConfirmSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : confirmLabel}
            </button>
          </>
        ) : (
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-accent">
            Close
          </button>
        )}
      </div>
    </Modal>
  );
};

export default PreviewModal;
